import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import {
  Sparkles, Trophy, Repeat, DollarSign, BadgeCheck, ShieldCheck,
  CalendarHeart, ArrowRight, FileText, Mic
} from 'lucide-react';

const JOIN_URL = 'https://charityfundraiser.com/find-an-auctioneer/join';
const PITCH_SHEET = 'https://charityfundraiser.com/api/auctioneers/marketing/pitch-sheet.pdf';

const benefits = [
  { icon: Trophy, title: 'Win more events', text: 'Walk into every pitch offering full-service technology — mobile bidding, live giving and checkout — not just the mic. Out-bid competitors who only bring a gavel.' },
  { icon: Repeat, title: 'Land & keep nonprofit clients', text: 'Become their year-round fundraising partner, not a once-a-year vendor. Clients who fundraise on your platform re-book you automatically.' },
  { icon: DollarSign, title: 'A new revenue stream', text: 'Earn on every event you bring on, with direct Stripe payouts to you. Add technology margin to your auctioneering fee.' },
  { icon: BadgeCheck, title: 'Your brand, front & center', text: 'Your logo, your name, your logins. Clients and donors see YOU — the platform runs quietly in the background.' },
  { icon: ShieldCheck, title: 'Better than the big names', text: 'No guilt-trip platform "tips" at donor checkout, transparent flat pricing, and direct payouts — a platform nonprofits actually prefer.' },
  { icon: CalendarHeart, title: 'Year-round tools', text: 'Donor CRM, recurring gifts, text-to-give, email and peer-to-peer campaigns keep clients raising — and paying you — between events.' },
];

const steps = [
  { n: '01', title: 'Apply', text: 'Tell us about your auctioneering business and the clients you serve.' },
  { n: '02', title: 'We brand it for you', text: 'We configure your white-labeled platform — logo, colors, domain and payouts.' },
  { n: '03', title: 'Go live in under an hour', text: 'Onboard your first nonprofit client and run a standout event under your name.' },
];

export const ForAuctioneers = () => {
  return (
    <>
      <Navbar />
      <Seo
        title="White-Label Fundraising Software for Auctioneers | Net Auctions"
        description="Win more events and nonprofit clients. White-label the CharityFundraiser platform under your own brand — mobile bidding, live paddle-raise, checkout, text-to-give and donor CRM."
        canonicalPath="/for-auctioneers"
      />
      <div className="min-h-screen bg-slate-950 text-white" data-testid="for-auctioneers-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-950 py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold mb-4">
              <Mic className="w-4 h-4" /> FOR AUCTIONEERS & BENEFIT-AUCTION PROS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
              Win more events with your own fundraising platform.
            </h1>
            <p className="text-lg text-indigo-100 max-w-3xl mb-8">
              White-label the complete CharityFundraiser platform under your brand. Show up to every pitch
              offering full-service technology, land more nonprofit clients, and earn a new revenue stream
              on every event you run.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={JOIN_URL} target="_blank" rel="noopener noreferrer" data-testid="apply-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-400 text-indigo-950 font-bold rounded-full hover:bg-amber-300 transition-colors shadow-lg">
                Apply to the program <ArrowRight className="w-5 h-5" />
              </a>
              <a href={PITCH_SHEET} target="_blank" rel="noopener noreferrer" data-testid="pitch-sheet-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-full hover:bg-white/20 transition-colors">
                <FileText className="w-5 h-5" /> Download the free pitch sheet
              </a>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-lg md:text-lg font-bold text-amber-300 mb-8 flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Why auctioneers white-label with us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-500/20 text-amber-300 mb-4">
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">{b.title}</h3>
                <p className="text-indigo-100/80 text-base">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white/5 border-y border-white/10 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-lg md:text-lg font-bold text-amber-300 mb-8">How it works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((s) => (
                <div key={s.n} className="relative">
                  <span className="text-5xl font-black text-indigo-500/30">{s.n}</span>
                  <h3 className="text-xl font-bold mt-2 mb-2">{s.title}</h3>
                  <p className="text-indigo-100/80">{s.text}</p>
                </div>
              ))}
            </div>
            <p className="text-indigo-200 mt-10 text-center">
              Free to explore · no upfront cost · your clients, your brand, your revenue.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 text-center">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Become a technology partner</h2>
            <p className="text-indigo-100 mb-8">
              Apply to the white-label program and start closing more nonprofit clients with a platform that
              carries your name.
            </p>
            <a href={JOIN_URL} target="_blank" rel="noopener noreferrer" data-testid="apply-cta-bottom"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-indigo-950 font-bold rounded-full hover:bg-amber-300 transition-colors shadow-lg">
              Apply now <ArrowRight className="w-5 h-5" />
            </a>
            <p className="text-sm text-indigo-300/70 mt-6">
              Already listed in our auctioneer directory? This is separate — here you become a technology partner.
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};
